import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CoolifyProjectDto,
  CoolifyServerDto,
  CoolifyStatusDto,
  MetricsStatusDto,
  ServerMetricsStateDto,
} from '@cpm/shared';
import { ExternalLink, FolderGit2, Gauge, RefreshCw, Server } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, ApiClientError } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { timeAgo } from '../components/charts.js';
import { DiskCapacityPanel } from '../components/disk-panels.js';
import { InstanceUpdatesPanel } from '../components/instance-updates.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
} from '../components/ui.js';

function SentinelServerRow({
  server,
  isAdmin,
}: {
  server: ServerMetricsStateDto;
  isAdmin: boolean;
}) {
  const queryClient = useQueryClient();
  const wantEnable = !(server.sentinelEnabled && server.metricsEnabled);
  const toggle = useMutation({
    mutationFn: () =>
      api.patch(`/api/metrics/servers/${server.serverUuid}`, {
        sentinelEnabled: wantEnable,
        metricsEnabled: wantEnable,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['metrics', 'status'] }),
  });
  // Coolify v4 rejects the flags via API (ADR-0012) — show the guidance
  // and the deep link the API sends back instead of a bare failure.
  const unsupported =
    toggle.error instanceof ApiClientError && toggle.error.code === 'SENTINEL_FLAGS_UNSUPPORTED'
      ? toggle.error
      : null;
  const coolifyServerUrl = (unsupported?.details as { coolifyServerUrl?: string } | undefined)
    ?.coolifyServerUrl;

  return (
    <li className="space-y-2 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{server.serverName}</p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {server.containersMonitored} monitored container
            {server.containersMonitored === 1 ? '' : 's'}
            {server.refreshRateSeconds ? ` · ${server.refreshRateSeconds}s sampling` : ''}
            {server.lastSuccessAt ? ` · collected ${timeAgo(server.lastSuccessAt)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={server.sentinelEnabled ? 'green' : 'gray'}>
            Sentinel {server.sentinelEnabled ? 'on' : 'off'}
          </Badge>
          <Badge tone={server.metricsEnabled ? 'green' : 'gray'}>
            Metrics {server.metricsEnabled ? 'on' : 'off'}
          </Badge>
          {server.sentinelEnabled && !server.tokenAvailable && (
            <Badge tone="red">token hidden</Badge>
          )}
          {isAdmin && (
            <Button
              variant="secondary"
              loading={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {wantEnable ? 'Enable metrics' : 'Disable metrics'}
            </Button>
          )}
        </div>
      </div>
      {server.lastError && (
        <p className="text-xs text-amber-600 dark:text-amber-400">{server.lastError}</p>
      )}
      {unsupported && (
        <p className="text-xs text-amber-600 dark:text-amber-400">
          {unsupported.message}
          {coolifyServerUrl && (
            <>
              {' '}
              <a
                href={coolifyServerUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 font-medium underline"
              >
                Open in Coolify <ExternalLink className="size-3" />
              </a>
            </>
          )}
        </p>
      )}
      {toggle.error != null && !unsupported && <ErrorNote error={toggle.error} />}
    </li>
  );
}

/**
 * Sentinel/metrics collection status per server (ADR-0012). Hidden
 * entirely when the metrics module is disabled (the API answers 404).
 */
function SentinelPanel() {
  const me = useMe();
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['metrics', 'status'],
    queryFn: () => api.get<MetricsStatusDto>('/api/metrics/status'),
    retry: false,
    refetchInterval: 60_000,
  });
  const collect = useMutation({
    mutationFn: () => api.post('/api/metrics/collect'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['metrics'] }),
  });

  if (status.isPending || status.error) return null;
  const isAdmin = me.data?.role === 'admin';
  const data = status.data;

  return (
    <div className="mt-4">
      <Card>
        <CardHeader
          title="Usage metrics (Sentinel)"
          description={`Collected every ${Math.round(data.intervalMs / 60_000)} min over SSH · ${data.retentionDays}-day retention · compose apps and service templates are not covered by Sentinel`}
          actions={
            <Button variant="secondary" loading={collect.isPending} onClick={() => collect.mutate()}>
              <RefreshCw className="size-4" /> Collect now
            </Button>
          }
        />
        <div className="px-5">
          {!data.sshKeyConfigured && (
            <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
              No server-access SSH key configured — collection cannot reach Sentinel.{' '}
              <Link to="/settings" className="font-medium underline">
                Add it under Settings → Usage metrics
              </Link>
              .
            </p>
          )}
          {collect.error != null && (
            <div className="mt-3">
              <ErrorNote error={collect.error} />
            </div>
          )}
          {data.servers.length === 0 ? (
            <EmptyState
              icon={<Gauge className="size-10" />}
              title="No collection data yet"
              description="Server Sentinel status appears after the first collection run."
            />
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.servers.map((server) => (
                <SentinelServerRow key={server.serverUuid} server={server} isAdmin={isAdmin} />
              ))}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

export function InfrastructurePage() {
  const me = useMe();
  const status = useQuery({
    queryKey: ['coolify', 'status'],
    queryFn: () => api.get<CoolifyStatusDto>('/api/coolify/status'),
  });
  const enabled = status.data?.reachable === true;

  const servers = useQuery({
    queryKey: ['coolify', 'servers'],
    queryFn: () => api.get<CoolifyServerDto[]>('/api/coolify/servers'),
    enabled,
  });
  const projects = useQuery({
    queryKey: ['coolify', 'projects'],
    queryFn: () => api.get<CoolifyProjectDto[]>('/api/coolify/projects'),
    enabled,
  });

  if (status.isPending) return <Spinner />;

  return (
    <div>
      <PageHeader
        title="Infrastructure"
        description="Your Coolify environment: servers and projects."
        actions={
          status.data?.reachable ? (
            <Badge tone="green">Coolify {status.data.coolifyVersion}</Badge>
          ) : status.data?.configured ? (
            <Badge tone="red">Unreachable</Badge>
          ) : (
            <Badge tone="yellow">Not configured</Badge>
          )
        }
      />

      {status.error != null && <ErrorNote error={status.error} />}

      {!status.data?.configured && (
        <Card>
          <EmptyState
            icon={<Server className="size-10" />}
            title="Coolify is not connected"
            description="Add your Coolify URL and API token to see servers and projects here."
            action={
              <Link
                to="/settings"
                className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-brand-700"
              >
                Open Settings
              </Link>
            }
          />
        </Card>
      )}

      {status.data?.configured && !status.data.reachable && (
        <ErrorNote error={new Error(status.data.error ?? 'Coolify instance is unreachable')} />
      )}

      {enabled && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader title="Servers" />
            {servers.isPending ? (
              <Spinner />
            ) : servers.error ? (
              <div className="p-5">
                <ErrorNote error={servers.error} />
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 px-5 dark:divide-slate-800">
                {servers.data.map((server) => (
                  <li key={server.uuid} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{server.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {server.ip}
                        {server.description ? ` · ${server.description}` : ''}
                      </p>
                    </div>
                    <div className="flex gap-1.5">
                      <Badge tone={server.reachable ? 'green' : 'red'}>
                        {server.reachable ? 'reachable' : 'unreachable'}
                      </Badge>
                      {server.usable && <Badge tone="blue">usable</Badge>}
                    </div>
                  </li>
                ))}
                {servers.data.length === 0 && (
                  <li className="py-6 text-center text-sm text-slate-500">No servers registered.</li>
                )}
              </ul>
            )}
          </Card>

          <Card>
            <CardHeader
              title="Projects"
              description="Projects created for subscriptions carry the panel's description"
            />
            {projects.isPending ? (
              <Spinner />
            ) : projects.error ? (
              <div className="p-5">
                <ErrorNote error={projects.error} />
              </div>
            ) : projects.data.length === 0 ? (
              <EmptyState
                icon={<FolderGit2 className="size-10" />}
                title="No projects"
                description="Projects appear here as they are created — directly in Coolify or by this panel."
              />
            ) : (
              <ul className="divide-y divide-slate-100 px-5 dark:divide-slate-800">
                {projects.data.map((project) => (
                  <li key={project.uuid} className="py-3">
                    <p className="text-sm font-medium">{project.name}</p>
                    {project.description && (
                      <p className="text-xs text-slate-500 dark:text-slate-400">{project.description}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {enabled && <SentinelPanel />}
      {enabled && <DiskCapacityPanel />}
      {me.data?.role === 'admin' && <InstanceUpdatesPanel />}
    </div>
  );
}
